import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { BaseComponent, FormatService, ReportTableColumn, SummaryItem } from '../../../../../tms-shared';
import { SalesOrderDepositReport, SalesOrderDepositReportLine } from '../../../../models';
import { SalesOrderReportsService, SalesOrderService } from '../../../../services';

@Component({
  selector: 'app-sales-deposit-report',
  templateUrl: './sales-deposit-report.component.html',
  styleUrl: './sales-deposit-report.component.scss',
})
export class SalesDepositReportComponent extends BaseComponent implements OnInit {
  @Input() salesOrderHeaderId: string;

  model$: Observable<SalesOrderDepositReport>;

  constructor(
    private salesOrderReportsService: SalesOrderReportsService,
    private salesOrderService: SalesOrderService,
    private formatService: FormatService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit() {
    if (!this.salesOrderHeaderId) {
      this.salesOrderHeaderId = this.route.snapshot.paramMap.get('id');
    }
    this.getModel();

    this.salesOrderService.refreshDetails.pipe(takeUntil(this.destroy$)).subscribe(() => this.getModel());
  }

  private getModel() {
    this.model$ = this.salesOrderReportsService.getSalesOrderDeposits(this.salesOrderHeaderId);
  }

  headerSummaryItems(model: SalesOrderDepositReport): SummaryItem[] {
    return [
      { label: $localize`Deposit Invoice Number`, value: model.orderNo },
      { label: $localize`Your Order Number`, value: model.customerOrderNo },
      { label: $localize`Your Order Reference`, value: model.customerOrderRef },
      { label: $localize`Customer Account`, value: model.customerKey },
      { label: $localize`Proforma Date`, value: this.formatService.date(model.dateOrdered) },
      { label: $localize`Ordered By`, value: model.orderedBy },
    ];
  }

  confirmationSummaryItems(model: SalesOrderDepositReport): SummaryItem[] {
    return model.customerConfirmationEmailAddress
      ? [{ label: $localize`Proforma emailed to`, value: model.customerConfirmationEmailAddress }]
      : [];
  }

  lineColumns(model: SalesOrderDepositReportLine): ReportTableColumn[] {
    return [
      {
        label: $localize`Deposit Notes`,
        value: model.notes,
      },
      {
        label: $localize`Date Entered`,
        value: this.formatService.date(model.dateEntered),
      },
      {
        label: $localize`Paid`,
        value: model.paid,
      },
      {
        label: $localize`Date Paid`,
        value: this.formatService.date(model.datePaid),
      },
      {
        label: $localize`Amount`,
        value: this.formatService.currency(model.amount, model.currencySymbol),
        textRight: true,
      },
    ];
  }

  footerSummaryItems(model: SalesOrderDepositReport): SummaryItem[][] {
    return [
      [
        { label: model.currency ? $localize`Currency` : '', value: model.currency },
        { label: model.agentName ? $localize`Agent` : '', value: model.agentName },
        { label: model.paymentTerms ? $localize`Payment Terms` : '', value: model.paymentTerms },
        { label: model.deliveryTerms ? $localize`Delivery Terms` : '', value: model.deliveryTerms },
        { label: model.deliveryTermNotes ? $localize`Delivery Term Notes` : '', value: model.deliveryTermNotes },
        { label: model.modeOfTransport ? $localize`Mode of Transport` : '', value: model.modeOfTransport },
        { label: model.shipper ? $localize`Shipper` : '', value: model.shipper },
        { label: model.shippingNotes ? $localize`Shipping Notes` : '', value: model.shippingNotes },
        { label: model.notes ? $localize`Notes` : '', value: model.documentNotes },
        {
          label: model.deliveryInstructions ? $localize`Delivery Instructions` : '',
          value: model.deliveryInstructions,
        },
        { label: $localize`Bank Name`, value: model.bankName },
        { label: $localize`Bank Account Name`, value: model.bankAccountName },
        { label: $localize`IBAN`, value: model.bankIBAN },
        { label: $localize`Swift`, value: model.bankSwift },
        { label: $localize`Sort Code`, value: `${model.bankSortCode ?? ''}` },
        { label: $localize`Account`, value: `${model.bankAccount ?? ''}` },
      ],
      [
        {
          label: $localize`Total Amount`,
          value: this.formatService.currency(model.depositValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`VAT` + ' @ ' + (model.taxRate > 0 ? ' ' + model.taxRate + '%' : ''),
          value: this.formatService.currency(model.taxValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`Overall Total`,
          value: this.formatService.currency(model.overallValue, model.currencySymbol),
          textRight: true,
        },
      ],
    ];
  }
}
